// The catalog assessment: which question comes next, how answers read back, and what the dashboard is sent.
// Everything here is shown to visitors, so it stays in plain business language: no prices, plans or technical terms.
import { type Answers, asTool, type CatalogSchema, type CatalogTool, type Listing, type PickId, type Scored } from "../../services/catalogApi";

export type QuestionId =
  | "problem" | "freq" | "volume" | "signoff" | "numbers"
  | "workspace" | "data" | "payment" | "tools" | "business" | "team";

/** Follow-up answers about the problem. Staff read them; the dashboard receives them as one plain note. */
export interface Details {
  problemText: string;
  freq: string;
  volume: string;
  signoff: string[];
  numbers: string[];
}

export const emptyDetails = (): Details => ({ problemText: "", freq: "", volume: "", signoff: [], numbers: [] });

export type Fit = "Strong" | "Good" | "Possible";

export interface QueueEntry {
  listing: Listing;
  reasons: string[];
  fit: Fit;
  /** A custom selection the dashboard put together; booking it needs this token. */
  token?: string;
  /** The visitor added it while browsing, so it was not matched to their answers and has no fit. */
  picked?: boolean;
}

export const SETUP_QUESTIONS: QuestionId[] = ["workspace", "data", "payment"];

export const FOLLOW_UPS: Record<"freq" | "volume" | "signoff", { title: string; help: string; options: [string, string][] }> = {
  freq: { title: "How often does this happen?", help: "A rough idea is fine.",
    options: [["daily", "Every day"], ["weekly", "Every week"], ["monthly", "Every month"], ["unsure", "I'm not sure"]] },
  volume: { title: "Roughly how many each month?", help: "Think bills, forms, enquiries or records.",
    options: [["lt100", "Under 100"], ["100to1000", "100 to 1,000"], ["gt1000", "More than 1,000"], ["unsure", "I'm not sure"]] },
  signoff: { title: "Who signs off today, and how?", help: "Tap every way approvals happen now.",
    options: [["email", "Email"], ["chat", "Chat"], ["paper", "Paper"], ["system", "A system"], ["unsure", "I'm not sure"]] },
};

export const NUMBERS = ["Sales", "Cash in the bank", "Open jobs", "Overdue invoices", "New leads"];

export const SETUP_HINTS: Record<string, string> = {
  microsoft: "Outlook, Teams, Excel", google: "Gmail, Drive, Sheets", several: "A mix that doesn't all connect",
  legacy: "A fine place to start", open: "Everyday business information", some: "Client, health or financial records",
  on_site: "Rules say it cannot leave your office", as_you_go: "Only for what you use", balance: "A bit of each",
  fixed: "The same amount every month",
};

export const NOT_FOR_ME = ["Not a priority right now", "We already have this", "Doesn't fit how we work", "Something else"];

const has = (text: string, words: RegExp) => words.test(text.toLowerCase());
const problemText = (answers: Answers, details: Details) => [...answers.problems, details.problemText].join(" ");

/** The questions for these answers, in order: the problem, up to three follow-ups chosen from it, then the setup. */
export function questionList(answers: Answers, details: Details): QuestionId[] {
  const text = problemText(answers, details);
  const follow: QuestionId[] = [];
  if (text.trim()) follow.push("freq");
  if (has(text, /bill|invoice|data|entry|document|lead|enquir|form|copy|typ/)) follow.push("volume");
  if (has(text, /approv|refund|sign/)) follow.push("signoff");
  if (has(text, /report|number|dashboard|spreadsheet/)) follow.push("numbers");
  return ["problem", ...follow.slice(0, 3), "workspace", "data", "payment", "tools", "business", "team"];
}

export const stageOf = (q: QuestionId) => (["problem", "freq", "volume", "signoff", "numbers"].includes(q) ? 0 : 1);

/** The tools question offers the catalog's own connectors: the dashboard already leaves out internal ones. */
export function toolChoices(schema: CatalogSchema): CatalogTool[] {
  return schema.tools.map(asTool);
}

export const monogram = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || name.slice(0, 2);

/** Tool name → brand mark, for chips that show a logo and fall back to initials. */
export function toolMarks(tools: (CatalogTool | string)[]): Map<string, string> {
  const marks = new Map<string, string>();
  for (const item of tools) {
    const tool = asTool(item);
    if (tool.logo) marks.set(tool.name, tool.logo);
  }
  return marks;
}

export function joinWords(items: string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

const label = (options: [string, string][], value: string) => options.find(([v]) => v === value)?.[1] ?? "";

/** A short reaction to the answer just given, shown above the next question. */
export function acknowledgement(q: QuestionId, answers: Answers, details: Details): string {
  switch (q) {
    case "problem":
      return answers.problems.length || details.problemText
        ? "Thanks, that's a common time sink. A few quick follow-ups."
        : "";
    case "freq":
      return ({ daily: "Every day adds up fast. That's where automation pays off most.",
                weekly: "Weekly work is a great fit for a set routine.",
                monthly: "Monthly tasks are easy to forget. We can make them run on time.",
                unsure: "No problem. We'll keep it flexible." } as Record<string, string>)[details.freq] ?? "";
    case "volume":
      return ({ lt100: "Even a few a week add up over a year.",
                "100to1000": "That's a solid amount to take off your team's plate.",
                gt1000: "At that volume, every minute saved counts.",
                unsure: "No problem. A rough guess is fine later." } as Record<string, string>)[details.volume] ?? "";
    case "signoff":
      return details.signoff.includes("unsure") ? "That's fine. We'll map it out together."
        : "Sign-offs spread across different places are hard to track. Good to know.";
    case "numbers":
      return details.numbers.length ? `${joinWords(details.numbers.slice(0, 2))} will go right at the top.` : "";
    case "payment":
      return "Noted. We'll go over pricing with you on a short call.";
    case "tools":
      return answers.tools.length ? "Noted. We'll connect them for you." : "No problem, we'll work out the tools together.";
    case "business":
      return "Thanks. That helps us pick examples that feel familiar.";
    default:
      return "";
  }
}

/** The follow-up answers as one short note for staff (the dashboard keeps it with the booking). */
export function detailsNote(details: Details): string {
  const parts = [];
  if (details.freq) parts.push(`How often: ${label(FOLLOW_UPS.freq.options, details.freq)}`);
  if (details.volume) parts.push(`Amount each month: ${label(FOLLOW_UPS.volume.options, details.volume)}`);
  if (details.signoff.length) {
    parts.push(`Sign-off today: ${details.signoff.map((s) => label(FOLLOW_UPS.signoff.options, s)).join(", ")}`);
  }
  if (details.numbers.length) parts.push(`Numbers checked first: ${details.numbers.join(", ")}`);
  return parts.join(". ").slice(0, 300);
}

/** Answers ready for the dashboard: the visitor's own problem words count as a problem too. */
export function answersForDashboard(answers: Answers, details: Details): Answers {
  const problems = details.problemText ? [...answers.problems, details.problemText] : answers.problems;
  return { ...answers, problems: [...new Set(problems)].slice(0, 20), volumes: detailsNote(details) };
}

/** Free text matched against the catalog's problem phrases by shared words. */
export function matchProblems(text: string, problems: string[]): string[] {
  const words = (s: string) => new Set(s.toLowerCase().split(/\W+/).filter((w) => w.length > 3));
  const said = words(text);
  return problems.filter((p) => [...words(p)].some((w) => [...said].some((s) => s.startsWith(w.slice(0, 5)))));
}

/** The dashboard explains why a listing matched; more reasons, earlier in the curated list, is a closer fit. */
export function fitFor(entry: Scored | undefined, temporary: boolean): Fit {
  if (temporary || !entry) return "Possible";
  if (entry.reasons.length >= 2) return "Strong";
  return entry.reasons.length === 1 ? "Good" : "Possible";
}

/** Plain reasons that tie the visitor's own answers to the listing, after the dashboard's. */
export function reasonRows(entry: QueueEntry, answers: Answers, details: Details, explanation: string | null) {
  const rows = entry.reasons.map((text) => ({ source: "Why it fits", text }));
  if (details.freq && details.freq !== "unsure") {
    rows.push({ source: "How often", text: `You said it happens ${label(FOLLOW_UPS.freq.options, details.freq).toLowerCase()}. It runs on its own, so nobody has to start it.` });
  }
  if (details.volume === "gt1000" || details.volume === "100to1000") {
    rows.push({ source: "How much", text: "It keeps up with the amount you handle, without extra staff." });
  }
  if (explanation) rows.push({ source: "Where it runs", text: explanation });
  if (answers.team_size === "Just me") rows.push({ source: "Your team", text: "It does the work without needing extra hands." });
  return rows.slice(0, 5);
}

export const PICK_OF: Record<string, PickId> = { workspace: "workspace", data: "data", payment: "payment" };
